import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Grid2 as Grid,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  InsertDriveFile,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  Visibility,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSubmission } from "../../../../store/actions/ppActions";

const PpNewSubmissionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [createdSubmission, setCreatedSubmission] = useState(null);

  const steps = ["Basic Information", "File Upload", "Review & Submit"];

  // Form validation schema
  const validationSchema = Yup.object({
    packageTier: Yup.string().required("Package tier is required"),
    permitNumber: Yup.string().when("hasPermitNumber", {
      is: true,
      then: (schema) => schema.required("Permit number is required"),
    }),
    address: Yup.string().required("Address is required"),
    contractor: Yup.string().required("Contractor is required"),
    type: Yup.string().required("Type is required"),
    city: Yup.string().required("City is required"),
    notes: Yup.string(),
    connectToPackage: Yup.boolean(),
    existingPackageId: Yup.string().when("connectToPackage", {
      is: true,
      then: (schema) => schema.required("Package ID is required"),
    }),
  });

  // Mock data
  const packageTiers = [
    { value: "residential_basic", label: "Residential Basic" },
    { value: "residential_standard", label: "Residential Standard" },
    { value: "residential_premium", label: "Residential Premium" },
    { value: "commercial_basic", label: "Commercial Basic" },
    { value: "commercial_standard", label: "Commercial Standard" },
    { value: "commercial_premium", label: "Commercial Premium" },
  ];

  const contractors = [
    { id: "C001", name: "ABC Construction", license: "CBC012345" },
    { id: "C002", name: "Best Builders LLC", license: "CBC054321" },
    { id: "C003", name: "Prime Contracting", license: "CBC098765" },
  ];

  const cities = [
    "Duval County",
    "St Johns County",
    "Clay County",
    "Nassau County",
  ];

  const formik = useFormik({
    initialValues: {
      packageTier: "",
      permitNumber: "",
      hasPermitNumber: false,
      address: "",
      contractor: "",
      type: "",
      city: "",
      notes: "",
      connectToPackage: false,
      existingPackageId: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (activeStep < steps.length - 1) {
        setActiveStep(activeStep + 1);
      } else {
        await handleSubmit(values);
      }
    },
  });

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      // Create submission data
      const submissionData = {
        ...values,
        files: uploadedFiles,
        packageTier: packageTiers.find((t) => t.value === values.packageTier)
          ?.label,
        contractor: contractors.find((c) => c.id === values.contractor),
        type: values.type,
        city: values.city,
        permitNumber: values.hasPermitNumber ? values.permitNumber : null,
      };

      const newSubmission = await dispatch(createSubmission(submissionData));
      setCreatedSubmission(newSubmission);
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error("Failed to create submission:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file, index) => ({
      id: `PRF${Date.now()}-${index + 1}-00`,
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
      file,
      uploadedAt: new Date().toISOString(),
    }));

    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const handleFileDelete = (fileId) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== fileId));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      formik.handleSubmit();
    } else if (activeStep === 1) {
      if (uploadedFiles.length === 0) {
        alert("Please upload at least one file");
        return;
      }
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const canProceedToNext = () => {
    if (activeStep === 0) {
      const requiredFields = [
        "packageTier",
        "address",
        "contractor",
        "type",
        "city",
      ];
      const hasRequiredFields = requiredFields.every(
        (field) => formik.values[field]
      );
      const permitValid =
        !formik.values.hasPermitNumber || formik.values.permitNumber;
      return hasRequiredFields && permitValid && !formik.errors.address;
    }
    if (activeStep === 1) {
      return uploadedFiles.length > 0;
    }
    return true;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid size={{ sm: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Package Tier</InputLabel>
                <Select
                  name="packageTier"
                  value={formik.values.packageTier}
                  label="Package Tier"
                  onChange={formik.handleChange}
                  error={
                    formik.touched.packageTier &&
                    Boolean(formik.errors.packageTier)
                  }
                >
                  {packageTiers.map((tier) => (
                    <MenuItem key={tier.value} value={tier.value}>
                      {tier.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ sm: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formik.values.type}
                  label="Type"
                  onChange={formik.handleChange}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                >
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ sm: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="hasPermitNumber"
                    checked={formik.values.hasPermitNumber}
                    onChange={formik.handleChange}
                  />
                }
                label="Permit number exists"
              />
            </Grid>

            {formik.values.hasPermitNumber && (
              <Grid size={{ sm: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="permitNumber"
                  label="Permit Number"
                  value={formik.values.permitNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.permitNumber &&
                    Boolean(formik.errors.permitNumber)
                  }
                  helperText={
                    formik.touched.permitNumber && formik.errors.permitNumber
                  }
                />
              </Grid>
            )}

            <Grid size={{ sm: 12 }}>
              <TextField
                fullWidth
                required
                name="address"
                label="Property Address"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>

            <Grid size={{ sm: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Contractor</InputLabel>
                <Select
                  name="contractor"
                  value={formik.values.contractor}
                  label="Contractor"
                  onChange={formik.handleChange}
                  error={
                    formik.touched.contractor &&
                    Boolean(formik.errors.contractor)
                  }
                >
                  {contractors.map((contractor) => (
                    <MenuItem key={contractor.id} value={contractor.id}>
                      {contractor.name} ({contractor.license})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ sm: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>City</InputLabel>
                <Select
                  name="city"
                  value={formik.values.city}
                  label="City"
                  onChange={formik.handleChange}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                >
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ sm: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    name="connectToPackage"
                    checked={formik.values.connectToPackage}
                    onChange={formik.handleChange}
                  />
                }
                label="Connect to existing inspection package"
              />
            </Grid>

            {formik.values.connectToPackage && (
              <Grid size={{ sm: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="existingPackageId"
                  label="Existing Package ID"
                  value={formik.values.existingPackageId}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.existingPackageId &&
                    Boolean(formik.errors.existingPackageId)
                  }
                  helperText={
                    formik.touched.existingPackageId &&
                    formik.errors.existingPackageId
                  }
                />
              </Grid>
            )}

            <Grid size={{ sm: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="notes"
                label="Notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                placeholder="Add any additional notes or special instructions..."
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Plan Review Files
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Upload PDF files for plan review. Files will be automatically
              named using the format:
              PRF[SubmissionID]-[FileNumber]-[RevisionNumber]
            </Alert>

            <Paper
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderColor: "grey.300",
                p: 4,
                textAlign: "center",
                mb: 3,
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              onClick={() => document.getElementById("file-upload").click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: "grey.500", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Click to upload files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: PDF (max 10MB per file)
              </Typography>
            </Paper>

            <input
              id="file-upload"
              type="file"
              multiple
              accept=".pdf"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />

            {uploadedFiles.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    Uploaded Files ({uploadedFiles.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => {
                      // Create temporary submission ID for preview
                      const tempId = `TEMP_${Date.now()}`;
                      navigate(`/pp/submissions/${tempId}/preview`);
                    }}
                  >
                    Preview Documents
                  </Button>
                </Box>
                <List>
                  {uploadedFiles.map((file) => (
                    <ListItem
                      key={file.id}
                      divider
                      secondaryAction={
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleFileDelete(file.id)}
                        >
                          <Delete />
                        </IconButton>
                      }
                    >
                      <InsertDriveFile sx={{ mr: 2, color: "error.main" }} />
                      <ListItemText
                        primary={file.name}
                        secondary={`${file.size} • ID: ${file.id}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Submission Details
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Package Tier:
                    </Typography>
                    <Typography variant="body1">
                      {
                        packageTiers.find(
                          (t) => t.value === formik.values.packageTier
                        )?.label
                      }
                    </Typography>
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Type:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {formik.values.type}
                    </Typography>
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Permit Number:
                    </Typography>
                    <Typography variant="body1">
                      {formik.values.hasPermitNumber
                        ? formik.values.permitNumber
                        : "Not assigned yet"}
                    </Typography>
                  </Grid>
                  <Grid size={{ sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      City:
                    </Typography>
                    <Typography variant="body1">
                      {formik.values.city}
                    </Typography>
                  </Grid>
                  <Grid size={{ sm: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      Address:
                    </Typography>
                    <Typography variant="body1">
                      {formik.values.address}
                    </Typography>
                  </Grid>
                  <Grid size={{ sm: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      Contractor:
                    </Typography>
                    <Typography variant="body1">
                      {
                        contractors.find(
                          (c) => c.id === formik.values.contractor
                        )?.name
                      }
                      (
                      {
                        contractors.find(
                          (c) => c.id === formik.values.contractor
                        )?.license
                      }
                      )
                    </Typography>
                  </Grid>
                  {formik.values.notes && (
                    <Grid size={{ sm: 12 }}>
                      <Typography variant="body2" color="text.secondary">
                        Notes:
                      </Typography>
                      <Typography variant="body1">
                        {formik.values.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Uploaded Files ({uploadedFiles.length})
                </Typography>
                <List dense>
                  {uploadedFiles.map((file) => (
                    <ListItem key={file.id}>
                      <InsertDriveFile sx={{ mr: 2, color: "error.main" }} />
                      <ListItemText
                        primary={file.name}
                        secondary={`${file.size} • ${file.id}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with back arrow */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/submissions")}>
          <ArrowBack />
        </IconButton>
        <Typography color="primary" variant="h4">
          Create New Plan Review Submission
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <form onSubmit={formik.handleSubmit}>
            {renderStepContent()}

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button
                onClick={handleBack}
                disabled={activeStep === 0}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/pp/submissions")}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={
                    activeStep === steps.length - 1
                      ? formik.handleSubmit
                      : handleNext
                  }
                  disabled={!canProceedToNext() || submitting}
                  endIcon={
                    activeStep === steps.length - 1 ? (
                      <CheckCircle />
                    ) : (
                      <ArrowForward />
                    )
                  }
                >
                  {activeStep === steps.length - 1
                    ? submitting
                      ? "Creating..."
                      : "Create Submission"
                    : "Next"}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircle color="success" />
            Submission Created Successfully
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Your submission has been created with ID:
            <strong> {createdSubmission?.id}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The submission is now available for review assignment and
            processing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate("/pp/submissions")}>
            View All Submissions
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/pp/submissions/${createdSubmission?.id}`)}
          >
            View Submission
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpNewSubmissionPage;
